import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Image } from "@/shared/components/ui/image";
import api from "@/consumer/lib/api";
import { useConsumerSession } from "@/consumer/hooks/use-consumer-session";
import {
  ConsumerFormattingProvider,
  ConsumerThemeProvider,
} from "@/consumer/components/tenant-branding";
import { defineField, formatDisplayValue, type FormFieldMeta } from "@/shared/lib/forms";
import { useFormatters } from "@/shared/lib/formatting";
import { getTenantSlugFromHost } from "@/shared/lib/tenant-host";

type LabelState =
  | "unrecognised"
  | "found"
  | "not_registered"
  | "registered"
  | "expired"
  | "pending"
  | "no_longer_valid"
  | "unavailable";

/** A product or registration field, raw, formatted here in the org's locale. */
type LabelField = {
  code_name: string;
  display_name: string;
  field_type: string;
  configuration_data: Record<string, unknown>;
  value: unknown;
};

type LabelScan = {
  state: LabelState;
  tenant_slug: string | null;
  brand_name: string | null;
  product_name: string | null;
  value: string | null;
  warranty_end: string | null;
  warranty_lifetime: boolean;
  extra: LabelField[];
  specs: LabelField[];
  actions: { register: boolean; claim: boolean };
  product_uuid: string | null;
  holding_uuid: string | null;
  attribute: string | null;
  attribute_label: string | null;
  image_url: string | null;
  thumbnail_url: string | null;
};

function registerHref(data: LabelScan): string {
  const params = new URLSearchParams();
  if (data.product_uuid) params.set("product", data.product_uuid);
  if (data.value) params.set("value", data.value);
  if (data.attribute) params.set("attribute", data.attribute);
  const query = params.toString();
  return `/register${query ? `?${query}` : ""}`;
}

export default function LabelScanPage() {
  const { token } = useParams<{ token: string }>();
  const { data: session, isPending: sessionLoading } = useConsumerSession();

  const query = useQuery({
    queryKey: ["label-scan", token, session?.uuid ?? "anon"],
    queryFn: () => api.get<LabelScan>(`/labels/${token}/`).then((r) => r.data),
    enabled: Boolean(token) && !sessionLoading,
    retry: 1,
  });

  // A generic host carries no company, so brand the page as the label's own.
  const labelTenant = getTenantSlugFromHost()
    ? undefined
    : query.data?.tenant_slug || undefined;

  return (
    <ConsumerThemeProvider slug={labelTenant}>
      <ConsumerFormattingProvider slug={labelTenant}>
        <LabelScanBody query={query} signedIn={Boolean(session?.uuid)} />
      </ConsumerFormattingProvider>
    </ConsumerThemeProvider>
  );
}

function LabelScanBody({
  query,
  signedIn,
}: {
  query: {
    data: LabelScan | undefined;
    isPending: boolean;
    isError: boolean;
    isFetching: boolean;
    refetch: () => unknown;
  };
  signedIn: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate, locale, currency } = useFormatters();
  const { data, isPending, isError, refetch, isFetching } = query;

  if (isPending) {
    return (
      <main className="mx-auto max-w-md px-5 py-16 text-[13px] text-[var(--ink-3)]">
        {t("common.loading", "Loading…")}
      </main>
    );
  }

  if (isError || !data || data.state === "unavailable") {
    return (
      <main className="mx-auto max-w-md px-5 py-16">
        <h1 className="text-center text-[22px] font-medium text-[var(--ink)]">
          {t("labelScan.unavailable", "This label could not be loaded.")}
        </h1>
        <p className="mt-2 text-center text-[13px] text-[var(--ink-3)]">
          {t("labelScan.unavailableHint", "Check your connection and try again.")}
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <Button disabled={isFetching} onClick={() => void refetch()}>
            {t("labelScan.retry", "Try again")}
          </Button>
        </div>
      </main>
    );
  }

  const known = data.state !== "unrecognised";
  const ownerView =
    signedIn && (data.state === "registered" || data.state === "expired");
  const show = (field: LabelField) =>
    formatDisplayValue(
      defineField({
        code_name: field.code_name,
        display_name: field.display_name,
        field_type: field.field_type as FormFieldMeta["field_type"],
        configuration_data: field.configuration_data,
      }),
      field.value,
      { locale, currency },
    );

  let heading = data.product_name || "";
  if (data.state === "unrecognised") {
    heading = t("labelScan.unrecognised", "This label is not recognised.");
  } else if (data.state === "no_longer_valid") {
    heading = t("labelScan.noLongerValid", "This label is no longer valid.");
  } else if (data.state === "pending") {
    heading = t("labelScan.pending", "Registration pending");
  } else if (ownerView && data.state === "expired") {
    heading = t("labelScan.expired", "Expired");
  } else if (ownerView && data.state === "registered") {
    heading = t("labelScan.registered", "Registered");
  }
  const specs = signedIn && known ? data.specs : [];
  const extra = signedIn && known ? data.extra : [];

  return (
    <main className="mx-auto max-w-md px-5 py-16">
      {known && (data.image_url || data.thumbnail_url) && (
        <Image
          src={data.image_url || data.thumbnail_url}
          thumbnailSrc={data.thumbnail_url}
          alt={data.product_name || ""}
          className="mx-auto mb-6 h-40 w-40 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
          fallback={<Package className="h-10 w-10 text-[var(--ink-3)]" />}
        />
      )}
      {known && data.brand_name && (
        <p className="mb-2 text-center text-[12px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          {data.brand_name}
        </p>
      )}
      {heading && (
        <h1 className="text-center text-[22px] font-medium text-[var(--ink)]">{heading}</h1>
      )}
      {ownerView && data.product_name && (
        <p className="mt-3 text-center text-[15px] text-[var(--ink)]">{data.product_name}</p>
      )}
      {ownerView && data.warranty_lifetime && (
        <p className="mt-2 text-[13px] text-[var(--ink-2)]">
          {t("labelScan.coveredForLife", "Covered for life")}
        </p>
      )}
      {ownerView && data.warranty_end && (
        <p className="mt-2 text-[13px] text-[var(--ink-2)]">
          {t("labelScan.warrantyEnd", {
            defaultValue: "Covered until {{date}}",
            date: formatDate(data.warranty_end),
          })}
        </p>
      )}
      {specs.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
          <p className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5 text-[12px] text-[var(--ink-3)]">
            {t("labelScan.specs", "Specifications")}
          </p>
          <dl className="divide-y-[0.5px] divide-[var(--rule)]">
            {specs.map((spec) => (
              <div
                key={spec.code_name}
                className="flex items-start justify-between gap-4 px-4 py-2.5"
              >
                <dt className="shrink-0 text-[12px] text-[var(--ink-3)]">
                  {spec.display_name}
                </dt>
                <dd className="text-right text-[12px] text-[var(--ink)]">{show(spec)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {extra.map((field) => (
        <p key={field.code_name} className="mt-2 text-[12px] text-[var(--ink-2)]">
          {field.display_name}: {show(field)}
        </p>
      ))}

      <div className="mt-8 flex flex-col gap-3">
        {data.actions.register && (
          <Button onClick={() => navigate(registerHref(data))}>
            {t("labelScan.register", "Register")}
          </Button>
        )}
        {data.actions.claim && data.holding_uuid && (
          <Button
            variant="outline"
            onClick={() => navigate(`/products/${data.holding_uuid}/submit-claim`)}
          >
            {t("labelScan.claim", "Claim")}
          </Button>
        )}
      </div>
    </main>
  );
}
